/* eslint-disable no-shadow */
import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import Checkout from "./Checkout";
import {
  Action,
  Badge,
  Feature,
  FeatureDescription,
  FeatureValue,
  NewPrice,
  OldPrice,
  PlanName,
  Popular,
  Price,
  PriceTag,
  AnnualPriceTag,
  Wrapper,
} from "./styles/plan-styles";
import { formatCurrency } from "../../helpers/formatters";

const Plan = ({
  accountManager,
  authenticityToken,
  badge,
  popular,
  buttonName,
  changePlanId,
  changePlanPath,
  sectors,
  currency,
  customFields,
  email,
  id,
  name,
  oldPrice,
  paymentTerms,
  planClass,
  price,
  publishableKey,
  serviceFee,
  taxPercent,
  userLimit,
  videoInterviewing,
  plansCount,
  index,
  paymentFrequency,
  stripeId,
}) => {
  const usersDescription = userLimit > 1 ? "users" : "user";
  const displayOldPrice = Number(oldPrice) / 100.0;
  const displayPrice = Number(price) / 100.0;
  const annualPriceMultiplier = planClass === "bi_annual_plan" ? 2 : 12;
  const annualPrice = parseInt(displayPrice * annualPriceMultiplier, 10);

  return (
    <Wrapper
      plansCount={plansCount}
      data-test-id="subscription-plan-card"
      className={`plan animated bounceIn delay${index}`}
    >
      <Price>
        <PlanName
          data-test-id="subscription-plan-name"
          nameLength={name.length}
        >
          {name}
        </PlanName>
        <PriceTag>
          {oldPrice && <OldPrice>{formatCurrency(displayOldPrice)}</OldPrice>}
          <NewPrice>{formatCurrency(displayPrice)}</NewPrice>
        </PriceTag>
        {paymentFrequency === "monthly" && (
          <AnnualPriceTag>{formatCurrency(annualPrice)}pa</AnnualPriceTag>
        )}
        {popular && <Popular>popular</Popular>}
        {badge && <Badge data-test-id="plan-badge">{badge}</Badge>}
      </Price>
      <Feature>
        <FeatureValue>{userLimit}</FeatureValue>
        <FeatureDescription>{usersDescription}</FeatureDescription>
      </Feature>
      <Feature>
        <FeatureValue>{sectors}</FeatureValue>
        <FeatureDescription>sectors</FeatureDescription>
      </Feature>
      <Feature>
        <FeatureValue>
          {serviceFee}
          {stripeId == "annual_pro_plan_2021" && "*"}
        </FeatureValue>
        <FeatureDescription>service fee</FeatureDescription>
      </Feature>
      {paymentTerms && (
        <Feature>
          <FeatureValue>{paymentTerms}</FeatureValue>
          <FeatureDescription>payment terms</FeatureDescription>
        </Feature>
      )}
      <Feature>
        <FeatureValue>
          <FontAwesomeIcon icon={faCheck} />
        </FeatureValue>
        {accountManager ? (
          <FeatureDescription>dedicated account manager</FeatureDescription>
        ) : (
          <FeatureDescription>telephone helpdesk</FeatureDescription>
        )}
      </Feature>
      {videoInterviewing && (
        <Feature>
          <FeatureValue>
            <FontAwesomeIcon icon={faCheck} />
          </FeatureValue>
          <FeatureDescription>video interviewing</FeatureDescription>
        </Feature>
      )}
      <Action>
        <Checkout
          authenticityToken={authenticityToken}
          buttonName={buttonName}
          currency={currency}
          customFields={customFields}
          email={email}
          id={id}
          name={name}
          planClass={planClass}
          price={price}
          displayPrice={displayPrice}
          publishableKey={publishableKey}
          taxPercent={taxPercent}
          changePlanId={changePlanId}
          changePlanPath={changePlanPath}
        />
      </Action>
    </Wrapper>
  );
};

Plan.propTypes = {
  accountManager: PropTypes.bool.isRequired,
  authenticityToken: PropTypes.string.isRequired,
  badge: PropTypes.string,
  buttonName: PropTypes.string.isRequired,
  changePlanId: PropTypes.string,
  changePlanPath: PropTypes.string,
  sectors: PropTypes.number,
  currency: PropTypes.string.isRequired,
  customFields: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  oldPrice: PropTypes.number,
  paymentTerms: PropTypes.string,
  planClass: PropTypes.string.isRequired,
  popular: PropTypes.bool,
  price: PropTypes.number.isRequired,
  publishableKey: PropTypes.string.isRequired,
  serviceFee: PropTypes.string.isRequired,
  taxPercent: PropTypes.number.isRequired,
  userLimit: PropTypes.number.isRequired,
  videoInterviewing: PropTypes.bool,
  plansCount: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  paymentFrequency: PropTypes.string.isRequired,
  stripeId: PropTypes.string.isRequired,
};

Plan.defaultProps = {
  badge: null,
  changePlanId: null,
  changePlanPath: null,
  sectors: null,
  oldPrice: null,
  paymentTerms: null,
  popular: null,
  videoInterviewing: null,
};

export default Plan;
